import { createClient } from '@supabase/supabase-js'

// Get environment variables with validation and cleaning
const rawUrl = process.env.REACT_APP_SUPABASE_URL
const rawKey = process.env.REACT_APP_SUPABASE_ANON_KEY

// Clean and validate environment variables more aggressively
const supabaseUrl = rawUrl?.trim()
  .replace(/\/$/, '') // Remove trailing slash
  .replace(/\s+/g, '') // Remove any whitespace
  .replace(/[^\w\-.:\/]/g, '') // eslint-disable-line no-useless-escape

const supabaseAnonKey = rawKey?.trim()
  .replace(/\s+/g, '') // Remove any whitespace
  .replace(/[^\w\-_.]/g, '') // Remove invalid JWT characters


// Validate environment variables more thoroughly
const isValidUrl = supabaseUrl && 
                   typeof supabaseUrl === 'string' &&
                   supabaseUrl.length > 0 &&
                   supabaseUrl.startsWith('https://') && 
                   supabaseUrl.includes('.supabase.co') &&
                   !supabaseUrl.includes(' ') // No spaces

const isValidKey = supabaseAnonKey && 
                   typeof supabaseAnonKey === 'string' &&
                   supabaseAnonKey.length > 100 && // JWT tokens are typically longer
                   !supabaseAnonKey.includes(' ') && // No spaces
                   /^[A-Za-z0-9\-_.]+$/.test(supabaseAnonKey) // Valid JWT characters

const hasValidConfig = isValidUrl && isValidKey


// Handle configuration errors
let supabaseClient
let errorMessage: string | null = null

if (!hasValidConfig) {
  const errorDetails = {
    url: {
      value: supabaseUrl || 'MISSING',
      valid: isValidUrl,
      issues: !isValidUrl ? [
        !supabaseUrl ? 'missing' : null,
        supabaseUrl && !supabaseUrl.startsWith('https://') ? 'no-https' : null,
        supabaseUrl && !supabaseUrl.includes('.supabase.co') ? 'not-supabase-domain' : null,
        supabaseUrl && supabaseUrl.includes(' ') ? 'contains-spaces' : null
      ].filter(Boolean) : []
    },
    key: {
      present: !!supabaseAnonKey,
      valid: isValidKey,
      length: supabaseAnonKey?.length || 0,
      issues: !isValidKey ? [
        !supabaseAnonKey ? 'missing' : null,
        supabaseAnonKey && supabaseAnonKey.length <= 100 ? 'too-short' : null,
        supabaseAnonKey && supabaseAnonKey.includes(' ') ? 'contains-spaces' : null,
        supabaseAnonKey && !/^[A-Za-z0-9\-_.]+$/.test(supabaseAnonKey) ? 'invalid-characters' : null
      ].filter(Boolean) : []
    },
    instruction: 'Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in environment variables'
  }
  
  console.error('Supabase configuration error:', errorDetails)
  errorMessage = 'Invalid Supabase configuration. Check environment variables.'
  
  // Always throw in development for easier debugging
  if (process.env.NODE_ENV === 'development') {
    throw new Error(`Invalid Supabase configuration: ${JSON.stringify(errorDetails, null, 2)}`)
  }
  
  // In production, provide a non-functional client that will show config error
  supabaseClient = null as any // Will be handled by configError export
} else {
  try {
    // Additional runtime validation before creating client
    if (!supabaseUrl.startsWith('https://') || !supabaseUrl.includes('.supabase.co')) {
      throw new Error('Invalid Supabase URL format')
    }
    
    if (supabaseAnonKey.length < 100 || !/^[A-Za-z0-9\-_.]+$/.test(supabaseAnonKey)) {
      throw new Error('Invalid Supabase anon key format')
    }
    
    supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: 'pkce'
      },
      global: {
        fetch: (url, options = {}) => {
          // Validate fetch parameters to prevent invalid value errors
          if (!url || typeof url !== 'string') {
            throw new Error('Invalid URL for fetch request')
          }
          
          // Ensure headers are properly formatted
          if (options.headers && typeof options.headers === 'object') {
            const headers = options.headers as Record<string, string>
            Object.keys(headers).forEach(key => {
              const value = headers[key]
              if (value && typeof value !== 'string') {
                headers[key] = String(value)
              }
            })
          }
          
          return fetch(url, options)
        }
      }
    })
  } catch (error) {
    console.error('Error creating Supabase client:', error)
    errorMessage = `Failed to initialize Supabase client: ${error instanceof Error ? error.message : 'Unknown error'}`
    supabaseClient = null as any
  }
}

export const supabase = supabaseClient
export const configError = errorMessage

export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string
          created_at: string
          email: string
          display_name: string | null
          role: string
        }
        Insert: {
          id: string
          created_at?: string
          email: string
          display_name?: string | null
          role?: string
        }
        Update: {
          id?: string
          created_at?: string
          email?: string
          display_name?: string | null
          role?: string
        }
      }
      storage_bins: {
        Row: {
          id: string
          created_at: string
          name: string
          description: string | null
          location: string | null
          user_id: string
        }
        Insert: {
          id?: string
          created_at?: string
          name: string
          description?: string | null
          location?: string | null
          user_id: string
        }
        Update: {
          id?: string
          created_at?: string
          name?: string
          description?: string | null
          location?: string | null
          user_id?: string
        }
      }
      cookie_cutters: {
        Row: {
          id: string
          created_at: string
          name: string
          description: string | null
          category: string | null
          photo_url: string
          bin_id: string | null
          user_id: string
        }
        Insert: {
          id?: string
          created_at?: string
          name: string
          description?: string | null
          category?: string | null
          photo_url: string
          bin_id?: string | null
          user_id: string
        }
        Update: {
          id?: string
          created_at?: string
          name?: string
          description?: string | null
          category?: string | null
          photo_url?: string
          bin_id?: string | null
          user_id?: string
        }
      }
      qr_codes: {
        Row: {
          id: string
          created_at: string
          bin_id: string
          qr_data: any
          user_id: string
        }
        Insert: {
          id?: string
          created_at?: string
          bin_id: string
          qr_data: any
          user_id: string
        }
        Update: {
          id?: string
          created_at?: string
          bin_id?: string
          qr_data?: any
          user_id?: string
        }
      }
      pending_cookie_cutters: {
        Row: {
          id: string
          created_at: string
          photo_url: string
          upload_session_id: string
          user_id: string
          status: 'uploaded' | 'processing' | 'completed' | 'failed'
          suggested_name: string | null
          suggested_category: string | null
          suggested_bin_id: string | null
          processing_error: string | null
          processed_at: string | null
        }
        Insert: {
          id?: string
          created_at?: string
          photo_url: string
          upload_session_id: string
          user_id: string
          status?: 'uploaded' | 'processing' | 'completed' | 'failed'
          suggested_name?: string | null
          suggested_category?: string | null
          suggested_bin_id?: string | null
          processing_error?: string | null
          processed_at?: string | null
        }
        Update: {
          id?: string
          created_at?: string
          photo_url?: string
          upload_session_id?: string
          user_id?: string
          status?: 'uploaded' | 'processing' | 'completed' | 'failed'
          suggested_name?: string | null
          suggested_category?: string | null
          suggested_bin_id?: string | null
          processing_error?: string | null
          processed_at?: string | null
        }
      }
      cookie_cutter_categories: {
        Row: {
          id: string
          created_at: string
          name: string
          user_id: string
        }
        Insert: {
          id?: string
          created_at?: string
          name: string
          user_id: string
        }
        Update: {
          id?: string
          created_at?: string
          name?: string
          user_id?: string
        }
      }
    }
  }
}